import { type FC, useEffect, useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { skipToken } from '@reduxjs/toolkit/query'
import * as Sentry from '@sentry/react'
import { useIntercom } from 'react-use-intercom'
import { Spinner } from '../../../components/Spinner/Spinner'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import {
  useGetAccountSchedulingSettingsQuery,
  useGetBookingByIdQuery,
  useGetSchedulingEventTypesQuery,
  useGetSchedulingLocationsQuery,
  useGetUserSchedulingLocationsQuery,
  useGetUserSchedulingSettingsQuery,
  useRescheduleBookingMutation,
} from '../../../store/scheduling/scheduling'
import { useGetUserByIdQuery } from '../../../store/user/user'
import {
  type Booking,
  type BookingClient,
  DeliveryMode,
  EmailTemplatePurpose,
  type Location,
  MeetingMode,
  type SchedulingEventType,
} from '../../../models/Scheduling'
import { buildReschedulePayload, canManageOwnLocations, isMeetingPlatformBroken, resolveViewTimezone } from './form/scheduleHelpers'
import { MeetingPlatformWarning } from '../shared/MeetingPlatformWarning'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { formatBookingDate, formatTimeRange } from '../bookingFormat'
import { ContactChips } from '../rows/ContactChips'
import { EventTypeChip } from '../rows/EventTypeChip'
import { ProviderBadge } from '../rows/ProviderBadge'
import { LocationSelect } from './form/LocationSelect'
import { SetTimeSection } from './form/SetTimeSection'
import { SchedulePreviewColumn } from './SchedulePreviewColumn'
import { SchedulingFlowHeader } from './SchedulingFlowHeader'
import { ScheduleSaveBar } from './ScheduleSaveBar'
import { STALE_TITLE, conflictNotice, errorNotice, saveBarNotices, type SaveBarNotice } from './saveBarNotice'
import { CLIENT_EMAIL_BLOCKS_SEND_REASON, noClientHasEmail } from './clientEmailGaps'
import { useCopyRenderedEmail } from './useCopyRenderedEmail'
import { useLinkBaseline } from './useLinkBaseline'
import { useLinkShareState } from './useLinkShareState'
import { noDraftRecipientsMessage, usePushBookingToDraft } from './usePushBookingToDraft'
import { buildEmailTemplateContext } from './buildEmailTemplateContext'
import { renderEmailHtmlBody, renderEmailSubject } from './renderEmailTemplate'
import { useSchedulingEmailTemplates } from '../shared/useSchedulingEmailTemplates'
import { useGetAccountQuery } from '../../../store/accountUserSettings/accountUserSettings'
import { formatTimezoneLabel } from '../shared/schedulingDisplay'
import { clientInviteUrl, collectClientInviteLinks, formatClientInviteLinks, withDraftInviteUrls } from './clientInviteLinks'
import { useUserTimezone } from '../../../common/useUserTimezone'
import { scheduleFormSchema, type ScheduleFormValues } from './scheduleFormSchema'
import { useDocumentTitle } from '../../../common/useDocumentTitle'

const FORM_ID = 'reschedule-meeting-form'

interface RescheduleMeetingFormProps {
  booking: Booking
  eventType?: SchedulingEventType
  ownerName: string
  accountLocations: Location[]
  accountDefaultLocationId?: string | null
  allowCustomLocation: boolean
}

const RescheduleMeetingForm: FC<RescheduleMeetingFormProps> = ({
  booking,
  eventType,
  ownerName,
  accountLocations,
  accountDefaultLocationId,
  allowCustomLocation,
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { trackingClient } = useTracking()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const userTimezone = useUserTimezone()
  const [rescheduleBooking, { isLoading: isSending }] = useRescheduleBookingMutation()
  const [draftBooking, setDraftBooking] = useState<Booking | null>(null)
  const [notice, setNotice] = useState<SaveBarNotice | null>(null)
  const { show: showSupport } = useIntercom()
  const { isEmailConnected, isPushing, pushToDraft } = usePushBookingToDraft()
  const copyRenderedEmail = useCopyRenderedEmail()
  const { byPurpose: templateByPurpose, isLoading: isTemplatesLoading, isError: isTemplatesError } = useSchedulingEmailTemplates()
  const { data: accountResponse } = useGetAccountQuery({ accountId })
  const getUserQuery = useGetUserByIdQuery({ accountId, userId: booking.userId })

  const form = useForm<ScheduleFormValues>({
    resolver: zodResolver(scheduleFormSchema),
    mode: 'onChange',
    shouldUnregister: false,
    defaultValues: {
      owner: getUserQuery.data?.data,
      eventType: eventType ?? null,
      clients: booking.clients,
      timeMode: 'specific-time',
      slot: null,
      suggestedDate: null,
      meetingMode: booking.meetingMode ?? MeetingMode.VIRTUAL,
      location: booking.location ?? null,
      note: '',
      title: booking.title ?? eventType?.name ?? '',
      invitees: booking.additionalInvitees ?? [],
      timezone: '',
      customLocation: null,
    },
  })
  const {
    control,
    setValue,
    handleSubmit,
    formState: { isValid },
  } = form
  const { isStale, isDraftStale, capture: captureLinkBaseline, captureDraft: captureDraftBaseline } = useLinkBaseline(form)

  const timeMode = useWatch({ control, name: 'timeMode' })
  const slot = useWatch({ control, name: 'slot' })
  const suggestedDate = useWatch({ control, name: 'suggestedDate' })
  const meetingMode = useWatch({ control, name: 'meetingMode' })
  const note = useWatch({ control, name: 'note' })
  const locationRef = useWatch({ control, name: 'location' })
  const title = useWatch({ control, name: 'title' })
  const timezone = useWatch({ control, name: 'timezone' })
  const invitees = useWatch({ control, name: 'invitees' })
  const previewClients = useMemo(() => withDraftInviteUrls(booking.clients, draftBooking), [booking.clients, draftBooking])
  const validate = { shouldValidate: true, shouldDirty: true }

  const { state: linkState, onLinkShared } = useLinkShareState(isStale)

  const incompleteReason = timeMode === 'specific-time' && slot == null ? 'Pick a time' : undefined
  const isSendBlockedByEmails = noClientHasEmail(booking.clients)

  const { data: userLocations } = useGetUserSchedulingLocationsQuery({ accountId, userId: booking.userId })
  const { data: userSettings } = useGetUserSchedulingSettingsQuery({ accountId, userId: booking.userId })
  const isOwnerPlatformBroken = userSettings ? isMeetingPlatformBroken(userSettings) : false

  const locations = [...accountLocations, ...(userLocations ?? [])]
  const selectedSavedLocation = locationRef?.id ? locations.find((location) => location.id === locationRef.id) : undefined
  const locationTimezoneLabel = selectedSavedLocation?.timeZone ? formatTimezoneLabel(selectedSavedLocation.timeZone) : ''

  const isInPersonMode = meetingMode === MeetingMode.IN_PERSON
  const viewTimezone = resolveViewTimezone({
    isInPerson: isInPersonMode,
    locationTimezone: selectedSavedLocation?.timeZone ?? '',
    selectedTimezone: timezone,
    userTimezone,
  })

  useEffect(() => {
    if (isInPersonMode && selectedSavedLocation?.timeZone && selectedSavedLocation.timeZone !== timezone) {
      setValue('timezone', selectedSavedLocation.timeZone)
    }
  }, [isInPersonMode, selectedSavedLocation?.timeZone, timezone, setValue])

  const runReschedule = async (data: ScheduleFormValues, delivery: DeliveryMode): Promise<Booking | null> => {
    const result = await rescheduleBooking({
      accountId,
      payload: buildReschedulePayload(booking.bookingId, data, delivery),
    })
    return 'error' in result ? null : result.data
  }

  const ensureDraftReschedule = async (data: ScheduleFormValues): Promise<Booking | null> => {
    if (draftBooking && !isDraftStale) {
      return draftBooking
    }
    const rescheduled = await runReschedule(data, DeliveryMode.DRAFT)
    if (rescheduled) {
      setDraftBooking(rescheduled)
      captureDraftBaseline()
    }
    return rescheduled
  }

  const onCopyEmail = (previewClient?: BookingClient) => {
    void handleSubmit(async (data) => {
      setNotice(null)
      if (isTemplatesLoading || isTemplatesError) {
        setNotice(errorNotice('Your email templates could not be loaded.'))
        return
      }
      const template = templateByPurpose(EmailTemplatePurpose.INVITATION_TO_REBOOK)
      if (!template || !template.subject || !template.body) {
        setNotice(errorNotice('There is no email template for this email yet. Configure one in Setup.', false))
        return
      }
      const recipient = previewClient ?? booking.clients[0]
      const rescheduled = await ensureDraftReschedule(data)
      if (!rescheduled || !eventType || !recipient) {
        if (!rescheduled) {
          setNotice(errorNotice('Something went wrong creating the email.'))
        }
        return
      }
      const link = clientInviteUrl(rescheduled, recipient)
      if (!link) {
        setNotice(errorNotice('The reschedule was saved, but it has no reschedule link — copy it from the Waiting tab.', false))
        return
      }
      const context = buildEmailTemplateContext({
        eventType,
        client: recipient,
        owner: getUserQuery.data?.data,
        note: data.note,
        meetingMode: data.meetingMode,
        location: selectedSavedLocation ?? null,
        bookingLink: link,
        firmName: accountResponse?.data.name,
        originalStart: booking.start,
      })
      const outcome = await copyRenderedEmail(renderEmailSubject(template.subject, context), renderEmailHtmlBody(template.body, context))
      if (outcome === 'copied') {
        trackingClient.track({ eventName: TrackEvent.SCHEDULING_INVITATION_EMAIL_COPIED, eventDetails: { flow: 'reschedule', bookingId: booking.bookingId } })
        captureLinkBaseline()
        onLinkShared()
        setNotice(saveBarNotices.emailCopied())
      } else {
        setNotice(errorNotice('Could not copy the email.'))
      }
    })()
  }

  const onCopyLink = handleSubmit(async (data) => {
    setNotice(null)
    const rescheduled = await ensureDraftReschedule(data)
    if (!rescheduled) {
      setNotice(saveBarNotices.copyLinkError('reschedule'))
      return
    }
    const links = collectClientInviteLinks(rescheduled.clients)
    if (links.length === 0) {
      setNotice(errorNotice('The reschedule was saved, but it has no reschedule link — copy it from the Waiting tab.', false))
      return
    }
    try {
      await navigator.clipboard.writeText(formatClientInviteLinks(links))
    } catch (error) {
      Sentry.captureException(error)
      setNotice(errorNotice('Could not copy the link. The reschedule was saved — copy it from the Waiting tab.', false))
      return
    }
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_BOOKING_LINK_COPIED, eventDetails: { flow: 'reschedule', bookingId: booking.bookingId } })
    captureLinkBaseline()
    onLinkShared()
    setNotice(saveBarNotices.linkCopied('reschedule', links.length))
  })

  const onUpdateBookingPage = handleSubmit(async (data) => {
    setNotice(null)
    const rescheduled = await runReschedule(data, DeliveryMode.DRAFT)
    if (!rescheduled) {
      setNotice(saveBarNotices.saveBookingError())
      return
    }
    setDraftBooking(rescheduled)
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_BOOKING_PAGE_UPDATED, eventDetails: { flow: 'reschedule', bookingId: booking.bookingId } })
    captureLinkBaseline()
    onLinkShared()
    setNotice(saveBarNotices.bookingPageUpdated())
  })

  const onSend = handleSubmit(async (data) => {
    setNotice(null)
    const result = await rescheduleBooking({
      accountId,
      payload: buildReschedulePayload(booking.bookingId, data, DeliveryMode.SEND),
    })
    if ('error' in result) {
      setNotice(
        conflictNotice(result.error) ??
          (data.timeMode === 'specific-time' ? saveBarNotices.sendInviteError() : saveBarNotices.sendEmailsError(booking.clients.length)),
      )
      return
    }
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_MEETING_RESCHEDULED, eventDetails: { timeMode: data.timeMode, bookingId: booking.bookingId } })
    if (data.timeMode === 'specific-time') {
      dispatch(addToast({ variant: 'success', title: 'Meeting rescheduled', messages: [`${result.data.title ?? 'The meeting'} moved to a new time.`] }))
      navigate('/scheduling/scheduled')
      return
    }
    dispatch(addToast({ variant: 'success', title: 'Reschedule request sent', messages: ['The client can now pick a new time.'] }))
    navigate('/scheduling/waiting')
  })

  const onPushToDraft = handleSubmit(async (data) => {
    setNotice(null)
    if (!eventType) {
      setNotice(errorNotice('This booking is missing its event type.', false))
      return
    }
    const rescheduled = await ensureDraftReschedule(data)
    if (!rescheduled) {
      setNotice(saveBarNotices.pushToDraftError(booking.clients.length))
      return
    }
    const pushResult = await pushToDraft({
      booking: rescheduled,
      eventType,
      owner: getUserQuery.data?.data,
      note: data.note,
      purpose: EmailTemplatePurpose.INVITATION_TO_REBOOK,
      location: selectedSavedLocation ?? null,
      originalStart: booking.start,
    })
    if (pushResult.outcome === 'pushed') {
      trackingClient.track({
        eventName: TrackEvent.SCHEDULING_INVITATION_PUSHED_TO_DRAFT,
        eventDetails: { flow: 'reschedule', drafts: pushResult.created, bookingId: booking.bookingId },
      })
      setNotice(
        pushResult.created < pushResult.total
          ? saveBarNotices.partialDrafts(pushResult.created, pushResult.total)
          : saveBarNotices.pushedToDraft(pushResult.created),
      )
    } else if (pushResult.outcome === 'backend-error') {
      setNotice(saveBarNotices.pushToDraftError(booking.clients.length))
    } else if (pushResult.reason === 'no-template') {
      setNotice(errorNotice('The reschedule was saved, but there is no email template for this email yet. Configure one in Setup.', false))
    } else if (pushResult.reason === 'no-recipients') {
      setNotice(errorNotice(noDraftRecipientsMessage('The reschedule was saved'), false))
    }
  })

  return (
    <div className='d-flex flex-column min-vh-100'>
      <div className='flex-grow-1 zocks-pb-10.0'>
        <Row>
          <Col lg={6} className='d-flex flex-column gap-4'>
            {isOwnerPlatformBroken ? <MeetingPlatformWarning /> : null}
            <div className='d-flex flex-column gap-3 p-3 border zocks-border-color-gray-200 zocks-border-1.0 zocks-bg-color-white'>
              {booking.start && booking.end ? (
                <div className='d-flex align-items-center zocks-gap-0.5 paragraph-small'>
                  <span className='zocks-text-gray-500'>{formatBookingDate(new Date(booking.start))}</span>
                  <span>{formatTimeRange(booking.start, booking.end)}</span>
                </div>
              ) : null}
              <div className='d-flex align-items-center flex-wrap zocks-gap-0.5'>
                <ContactChips clients={[...booking.clients, ...(booking.additionalInvitees ?? [])]} />
                <span className='zocks-text-gray-600'>·</span>
                <EventTypeChip eventType={eventType} />
                <span className='zocks-text-gray-600'>·</span>
                <ProviderBadge meetingMode={booking.meetingMode} meetingUrl={booking.meetingUrl} />
              </div>
            </div>
            <form id={FORM_ID} className='d-flex flex-column gap-4' onSubmit={onSend}>
              {eventType ? (
                <>
                  <SetTimeSection
                    mode={timeMode}
                    eventTypeId={eventType.id}
                    userId={booking.userId}
                    meetingMode={meetingMode}
                    timezone={viewTimezone}
                    locationTimezoneLabel={locationTimezoneLabel}
                    selectedSlot={slot}
                    suggestedDate={suggestedDate}
                    onModeChange={(value) => setValue('timeMode', value, validate)}
                    onSlotChange={(value) => setValue('slot', value, validate)}
                    onSuggestedDateChange={(value) => setValue('suggestedDate', value, validate)}
                    onTimezoneChange={(value) => setValue('timezone', value)}
                  />
                  {meetingMode === MeetingMode.IN_PERSON ? (
                    <LocationSelect
                      eventType={eventType}
                      accountDefaultLocationId={accountDefaultLocationId}
                      userDefaultLocationId={userSettings?.defaults.locationId}
                      accountLocations={accountLocations}
                      userLocations={userLocations ?? []}
                      ownerUserId={booking.userId}
                      ownerFirstName={ownerName.split(' ')[0]}
                      isSchedulingForSelf={booking.userId === authenticatedUser.id}
                      location={locationRef}
                      customLocation={null}
                      allowCustomLocation={allowCustomLocation}
                      onChange={(next) => {
                        if (next.location !== undefined) {
                          setValue('location', next.location, validate)
                        }
                      }}
                    />
                  ) : null}
                </>
              ) : null}
            </form>
          </Col>
          <Col lg={6}>
            <SchedulePreviewColumn
              eventType={eventType}
              mode={timeMode}
              emptyState={
                <div className='d-flex align-items-center justify-content-center text-center p-4 border zocks-border-color-gray-200 zocks-border-1.0 paragraph-medium zocks-text-gray-500'>
                  This booking is missing its event type, so it can&apos;t be rescheduled here.
                </div>
              }
              emailPurpose={EmailTemplatePurpose.INVITATION_TO_REBOOK}
              owner={getUserQuery.data?.data}
              ownerName={ownerName}
              invitees={invitees}
              meetingMode={meetingMode}
              meetingPlatform={userSettings?.integrations.selectedMeetingPlatform}
              joinLinkBroken={isOwnerPlatformBroken}
              slot={slot}
              title={title}
              timeZone={viewTimezone}
              timezoneLabel={isInPersonMode ? locationTimezoneLabel : viewTimezone ? formatTimezoneLabel(viewTimezone) : ''}
              location={selectedSavedLocation ?? null}
              note={note}
              onNoteChange={(value) => setValue('note', value, validate)}
              clients={previewClients}
              originalStart={booking.start}
              onCopyEmail={onCopyEmail}
              copyEmailDisabled={!isValid || isSending || linkState === 'stale'}
              copyEmailDisabledReason={linkState === 'stale' ? STALE_TITLE : incompleteReason}
            />
          </Col>
        </Row>
      </div>
      <ScheduleSaveBar
        formId={FORM_ID}
        mode={timeMode}
        isValid={isValid}
        isSubmitting={isSending || isPushing}
        linkState={linkState}
        linkNoun='reschedule'
        notice={notice}
        onDismissNotice={() => setNotice(null)}
        incompleteReason={incompleteReason}
        onContactSupport={showSupport}
        onCopyLink={onCopyLink}
        onUpdateBookingPage={onUpdateBookingPage}
        onPushToDraft={onPushToDraft}
        pushToDraftDisabled={!isEmailConnected}
        pushToDraftDisabledReason='Connect your email account to push drafts.'
        submitDisabled={isSendBlockedByEmails}
        submitDisabledReason={CLIENT_EMAIL_BLOCKS_SEND_REASON}
      />
    </div>
  )
}

export const RescheduleMeetingFlow: FC = () => {
  const navigate = useNavigate()
  const { bookingId = '' } = useParams()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const bookingQuery = useGetBookingByIdQuery(bookingId ? { accountId, bookingId } : skipToken)
  const eventTypesQuery = useGetSchedulingEventTypesQuery({ accountId })
  const { data: accountSettings } = useGetAccountSchedulingSettingsQuery({ accountId })
  const { data: locations } = useGetSchedulingLocationsQuery({ accountId })
  useDocumentTitle('Scheduling | Zocks')

  const booking = bookingQuery.data
  const ownerUserId = booking?.userId
  const isOwnerSelf = ownerUserId === authenticatedUser.id
  const ownerQuery = useGetUserByIdQuery(ownerUserId && !isOwnerSelf ? { accountId, userId: ownerUserId } : skipToken)
  const eventType = booking?.eventTypeId ? eventTypesQuery.data?.find((candidate) => candidate.id === booking.eventTypeId) : undefined
  const ownerName = getUserDisplayName(isOwnerSelf ? authenticatedUser : (ownerQuery.data?.data ?? authenticatedUser), 'Me')
  const isPending = bookingQuery.isLoading || eventTypesQuery.isLoading || ownerQuery.isLoading

  return (
    <Container>
      <SchedulingFlowHeader title='Reschedule a meeting' onBack={() => navigate(-1)} onClose={() => navigate(-1)} />
      {isPending ? (
        <div className='d-flex justify-content-center py-5'>
          <Spinner />
        </div>
      ) : booking ? (
        <RescheduleMeetingForm
          booking={booking}
          eventType={eventType}
          ownerName={ownerName}
          accountLocations={locations ?? []}
          accountDefaultLocationId={accountSettings?.defaults.locationId}
          allowCustomLocation={canManageOwnLocations(accountSettings?.userControls)}
        />
      ) : (
        <div className='d-flex justify-content-center py-5 paragraph-medium zocks-text-gray-500'>This booking could not be found.</div>
      )}
    </Container>
  )
}
